import { resolveFirstLoadingImageUrl } from "./resolveFirstLoadingImageUrl";
import { sportImagePublicBase } from "./sportImageCdn";

const DEFAULT_TENNIS_PLAYER_IMAGE_S3_PREFIX = "Tennis/Player Images";

export { isTennisNationTeamCompetition, parseTennisNationTeamLabel } from "./tennisNationTeamImage";
export type { TennisNationTeamLabel } from "./tennisNationTeamImage";

import { parseTennisNationTeamLabel } from "./tennisNationTeamImage";

/** Headshots at `Tennis/Player Images/{slug}.png` (CloudFront → cricket8-web-content). */
export const TENNIS_PLAYER_IMAGE_S3_BASE = sportImagePublicBase(
  DEFAULT_TENNIS_PLAYER_IMAGE_S3_PREFIX,
  process.env.TENNIS_PLAYER_IMAGE_BASE
);

/** Split "Harri Heliovaara/Henry Patten" into individual doubles partners. */
export const splitTennisDoublesPlayerNames = (playerName: string): string[] => {
  const raw = String(playerName || "").trim();
  if (!raw || !raw.includes("/")) return raw ? [raw] : [];

  const parts = raw.split(/\s*\/\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length !== 2) return [raw];

  const looksLikePlayerName = (part: string): boolean =>
    /[a-zA-Z]/.test(part) && !/^\d+\s*\/\s*\d+$/.test(part) && !/^(over|under)\b/i.test(part);

  if (!looksLikePlayerName(parts[0]) || !looksLikePlayerName(parts[1])) return [raw];
  return parts;
};

export const isTennisDoublesPlayerName = (playerName: string): boolean =>
  splitTennisDoublesPlayerNames(playerName).length === 2;

export const slugifyTennisPlayerName = (name: string): string =>
  String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\./g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const cleanNameToken = (token: string): string => String(token || "").replace(/\./g, "").trim();

const isLikelyInitial = (token: string): boolean => {
  const t = cleanNameToken(token);
  return t.length >= 1 && t.length <= 2;
};

const splitPlayerNameParts = (name: string): string[] =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

/**
 * Name variants for S3 image lookup.
 * Never emit a bare surname — shared surnames collide (e.g. Zheng).
 * Doubles "Surname1/Surname2" labels need rankings → full names first.
 * Keep initial+surname ("S. Errani" → s-errani) for feed-friendly aliases.
 */
export const playerNameVariantsForImageLookup = (playerName: string): string[] => {
  const variants: string[] = [];
  const seen = new Set<string>();
  const addVariant = (value: string) => {
    const trimmed = String(value || "").trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    variants.push(trimmed);
  };

  const addInitialSurname = (firstClean: string, lastClean: string) => {
    if (firstClean.length < 2 || lastClean.length <= 2) return;
    if (isLikelyInitial(firstClean)) return;
    addVariant(`${firstClean[0]} ${lastClean}`);
  };

  const raw = String(playerName || "").trim();
  if (!raw) return variants;

  addVariant(raw);

  const nationTeam = parseTennisNationTeamLabel(raw);
  if (nationTeam) {
    addVariant(nationTeam.country);
  }

  const comma = raw.indexOf(",");
  if (comma > 0) {
    const last = raw.slice(0, comma).trim();
    const first = raw.slice(comma + 1).trim();
    if (first && last) {
      addVariant(`${first} ${last}`);
      addInitialSurname(cleanNameToken(first), cleanNameToken(last));
    }
  }

  if (!raw.includes(",")) {
    const parts = splitPlayerNameParts(raw);

    if (parts.length === 2) {
      const first = parts[0];
      const last = parts[1];
      const firstClean = cleanNameToken(first);
      const lastClean = cleanNameToken(last);

      if (!nationTeam) {
        if (isLikelyInitial(first) && lastClean.length > 2) {
          // "S. Errani" — keep raw only (→ s-errani). No bare surname.
        } else if (firstClean.length > 2 && lastClean.length > 2) {
          addVariant(`${lastClean}, ${firstClean}`);
          addInitialSurname(firstClean, lastClean);
        }

        if (parts[1].length <= 2) {
          addVariant(`${parts[1]} ${parts[0]}`.replace(/\./g, "").trim());
        }
      }
    }

    if (parts.length >= 3) {
      const firstClean = cleanNameToken(parts[0]);
      const lastClean = cleanNameToken(parts[parts.length - 1]);
      if (firstClean.length > 1 && lastClean.length > 2) {
        addVariant(`${firstClean} ${lastClean}`);
        addVariant(`${lastClean}, ${firstClean}`);
        addInitialSurname(firstClean, lastClean);
      }
    }
  }

  return variants;
};

export const resolveTennisPlayerImageSlugCandidates = (playerName: string): string[] => {
  const slugs: string[] = [];
  const seen = new Set<string>();
  const addSlug = (variant: string) => {
    const slug = slugifyTennisPlayerName(variant);
    if (slug && !seen.has(slug)) {
      seen.add(slug);
      slugs.push(slug);
    }
  };

  const nationTeam = parseTennisNationTeamLabel(playerName);
  if (nationTeam) {
    addSlug(nationTeam.country);
  }

  for (const variant of playerNameVariantsForImageLookup(playerName)) {
    addSlug(variant);
  }
  return slugs;
};

export function tennisPlayerImageS3Url(slug: string): string {
  const safe = slugifyTennisPlayerName(slug) || slug;
  return `${TENNIS_PLAYER_IMAGE_S3_BASE}${encodeURIComponent(safe)}.png`;
}

export function resolveTennisPlayerImageS3UrlsFromName(playerName: string): string[] {
  return resolveTennisPlayerImageSlugCandidates(playerName).map((slug) => tennisPlayerImageS3Url(slug));
}

export function isGoalserveTennisPlayerImage(value: unknown): value is string {
  if (typeof value !== "string" || !value) return false;
  return value.startsWith("data:image") || value.startsWith("/9j/") || value.startsWith("iVBOR");
}

/**
 * First tennis player headshot URL that loads successfully, or null.
 */
export function resolveTennisPlayerImageUrl(playerName: string): Promise<string | null> {
  return resolveFirstLoadingImageUrl(resolveTennisPlayerImageS3UrlsFromName(playerName));
}
